import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, MoreVertical } from 'lucide-react';
import { supabase } from '../../lib/supabase';

/**
 * Master admins tab — Instacut staff with full cross-shop access.
 * Handles: list + invite + pause/resume + delete. Row actions are
 * routed through the outside-click-closing "…" menu; the pause toggle
 * is inline for one-tap access.
 */
export const MasterAdminsPanel = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [rowBusy, setRowBusy] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);

  const call = useCallback(async (path, method = 'GET') => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      method,
      headers: { Authorization: `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
    });
    const text = await res.text();
    let j = {}; try { j = JSON.parse(text); } catch (_e) { /* keep raw */ }
    if (!res.ok) throw new Error(j.detail || text || `HTTP ${res.status}`);
    return j;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const j = await call('/api/auth/master-admins');
      setAdmins(j.admins || []);
    } catch (e) {
      setMsg(`Failed to load: ${e.message}`);
    } finally { setLoading(false); }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  // Close the row action menu on any outside click / Escape.
  useEffect(() => {
    if (!menuOpenId) return undefined;
    const onDoc = (e) => { if (!e.target.closest('.ma-menu-root')) setMenuOpenId(null); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpenId(null); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpenId]);

  const invite = async () => {
    setBusy(true); setMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/create-master-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ email: email.trim().toLowerCase(), name: name.trim() || null }),
      });
      const text = await res.text();
      let j = {}; try { j = JSON.parse(text); } catch (_e) { /* keep raw */ }
      if (!res.ok) throw new Error(j.detail || text || `HTTP ${res.status}`);
      setMsg(`Master admin created — reset link emailed to ${email}.`);
      setShowInvite(false);
      setEmail(''); setName('');
      await load();
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  const togglePause = async (a) => {
    setRowBusy(a.id); setMsg('');
    // Optimistic flip so the toggle animates immediately.
    setAdmins((prev) => prev.map((x) => (x.id === a.id ? { ...x, is_paused: !x.is_paused } : x)));
    try {
      await call(`/api/auth/master-admin/${a.id}/toggle-active`, 'POST');
    } catch (e) {
      // Revert on failure and surface the reason.
      setAdmins((prev) => prev.map((x) => (x.id === a.id ? { ...x, is_paused: a.is_paused } : x)));
      setMsg(`Failed: ${e.message}`);
    } finally { setRowBusy(null); }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Delete master admin ${a.full_name || a.email}? This removes their login and profile.`)) return;
    setRowBusy(a.id); setMsg('');
    try {
      await call(`/api/auth/master-admin/${a.id}`, 'DELETE');
      setAdmins((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    } finally { setRowBusy(null); }
  };

  return (
    <div data-testid="master-admins-panel">
      <h3 className="admin-info-header">
        Master admins
        <span className="plan-sub">Instacut staff with full cross-shop access. No shop attached.</span>
      </h3>

      {showInvite && (
        <div
          style={{ background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 10, padding: 14, margin: '4px 0 4px' }}
          data-testid="invite-master-panel"
        >
          <div className="field">
            <label>Email</label>
            <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. name@instacut.co.uk" data-testid="invite-master-email" />
          </div>
          <div className="field">
            <label>Name <span className="hint" style={{ fontWeight: 'var(--fw-regular)' }}>(optional)</span></label>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="invite-master-name" />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button className="btn p" disabled={!email.trim() || busy} onClick={invite} data-testid="invite-master-submit">
              {busy ? 'Inviting…' : 'Send invite'}
            </button>
            <button className="btn s" onClick={() => { setShowInvite(false); setMsg(''); }} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {msg && (
        <div
          className={msg.startsWith('Failed') ? 'alert error' : 'alert success'}
          style={{ margin: '4px 0 6px', fontSize: 'var(--fs-sm)' }}
          data-testid="master-admins-msg"
        >{msg}</div>
      )}

      {loading ? (
        <div className="set-row"><div className="tx"><small>Loading…</small></div></div>
      ) : admins.length === 0 ? (
        <div className="set-row"><div className="tx"><small>No master admins yet.</small></div></div>
      ) : admins.map((a) => (
        <div className="set-row" key={a.id} data-testid={`master-admin-row-${a.id}`}>
          <div className="tx" style={{ minWidth: 0, flex: 1 }}>
            <b style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {a.full_name || '—'}
              {a.is_self && <span className="adm-pill pill self">You</span>}
            </b>
            <small>
              {a.email || 'no email'}
              {a.created_at && ` · added ${new Date(a.created_at).toLocaleDateString('en-GB')}`}
            </small>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              className={`tog ${a.is_paused ? 'off' : ''}`}
              disabled={a.is_self || rowBusy === a.id}
              onClick={() => togglePause(a)}
              aria-label={a.is_paused ? `Resume access for ${a.full_name || a.email}` : `Pause access for ${a.full_name || a.email}`}
              title={a.is_self ? "You can't pause your own account" : (a.is_paused ? 'Paused — click to resume' : 'Active — click to pause')}
              data-testid={`master-admin-toggle-${a.id}`}
            />
            <div className="ma-menu-root" style={{ position: 'relative' }}>
              <button
                type="button"
                className="bcard-menu-btn"
                onClick={() => setMenuOpenId((prev) => (prev === a.id ? null : a.id))}
                aria-haspopup="menu"
                aria-expanded={menuOpenId === a.id}
                disabled={a.is_self}
                title={a.is_self ? "You can't act on your own account" : 'Actions'}
                data-testid={`master-admin-actions-${a.id}`}
              >
                <MoreVertical size={16} />
              </button>
              {menuOpenId === a.id && (
                <div className="bcard-menu" role="menu" data-testid={`master-admin-menu-${a.id}`}>
                  <button
                    type="button"
                    role="menuitem"
                    className="bcard-menu-item danger"
                    onClick={() => { setMenuOpenId(null); removeAdmin(a); }}
                    disabled={rowBusy === a.id}
                    data-testid={`master-admin-delete-${a.id}`}
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      {!showInvite && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '14px 0 4px' }}>
          <button className="btn s" onClick={() => { setShowInvite(true); setMsg(''); }} data-testid="invite-master-btn">
            <Plus size={13} /> Invite master admin
          </button>
        </div>
      )}
    </div>
  );
};
